import { Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { IUser } from '@core/interfaces/user.interface';
import { AuthService } from '@core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isAfter } from 'date-fns';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime } from 'rxjs/operators';
import { UserGroupSelectStore } from './user-group-select.store';

@Component({
  imports: [
    MatSelectModule,
    TranslateModule,
    MatFormFieldModule,
    HasPermissionDirective,
    DotsSpinnerComponent,
    IconComponent,
    NgxMatSelectSearchModule,
    ReactiveFormsModule,
  ],
  providers: [UserGroupSelectStore],
  selector: 'app-user-group-select',
  styleUrls: ['./user-group-select.component.scss'],
  templateUrl: './user-group-select.component.html',
})
export class UserGroupSelectComponent extends BaseComponent {
  public readonly currentDbUser: IUser | null = AuthService.currentDbUser;

  public readonly searchTerm = signal<string>('');
  public readonly userGroupSearchControl: FormControl<string | null> = new FormControl<string | null>('');

  public readonly filteredUserGroups = computed(() => {
    this.recalculateTrigger();

    const filtered =
      this.currentDbUser?.userGroups?.filter(
        (userGroup) =>
          !userGroup.endDateRead || isAfter(this.dateUtils.parseDateTimeFromApi(userGroup.endDateRead), new Date()),
      ) ?? [];

    const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));
    const term = this.searchTerm().toLowerCase();
    const selectedUserGroupId = this.currentDbUser?.userGroupId;

    let result = term ? sorted.filter((userGroup) => userGroup.name.toLowerCase().includes(term)) : sorted;

    if (selectedUserGroupId) {
      const selectedIndex = result.findIndex((userGroup) => userGroup.id === selectedUserGroupId);

      if (selectedIndex > -1) {
        result = [result[selectedIndex], ...result.filter((userGroup) => userGroup.id !== selectedUserGroupId)];
      }
    }

    return result;
  });

  public readonly store = inject(UserGroupSelectStore);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly recalculateTrigger = signal<boolean>(false);

  constructor() {
    super();

    effect(() => {
      if (this.store.userGroup$() && !this.store.error$()) {
        window.location.reload();
      }
    });

    effect(() => {
      if (this.authService.userGroupsChanged()) {
        this.recalculateTrigger.set(!untracked(this.recalculateTrigger));
      }
    });

    this.subscribeToSearchChanges();
  }

  private subscribeToSearchChanges(): void {
    this.userGroupSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchValue: string | null) => {
        this.searchTerm.set(searchValue ?? '');
      });
  }

  public onUserGroupChange(userGroupId: string): void {
    this.store.switchUserGroup(userGroupId);
  }
}
